import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listPipelines,
  createPipeline,
  updatePipeline,
  deletePipeline,
  createStage,
  updateStage,
  deleteStage,
  reorderStages,
  type CreatePipelineInput,
  type UpdatePipelineInput,
  type CreateStageInput,
  type UpdateStageInput,
} from '../lib/leadsApi';

// React Query hooks for lead pipeline & stage configuration (CLE-193).
// The shared `['lead-pipelines']` key is invalidated after every mutation so
// the Settings UI and the Leads board (which reads the same key) reflect
// changes immediately.

export const LEAD_PIPELINES_KEY = ['lead-pipelines'] as const;

export function useLeadPipelines() {
  return useQuery({
    queryKey: LEAD_PIPELINES_KEY,
    queryFn: listPipelines,
  });
}

export function useCreatePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePipelineInput) => createPipeline(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LEAD_PIPELINES_KEY });
    },
  });
}

export function useUpdatePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdatePipelineInput }) =>
      updatePipeline(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LEAD_PIPELINES_KEY });
    },
  });
}

export function useDeletePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deletePipeline(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LEAD_PIPELINES_KEY });
    },
  });
}

export function useCreateStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pipelineId, input }: { pipelineId: string; input: CreateStageInput }) =>
      createStage(pipelineId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LEAD_PIPELINES_KEY });
    },
  });
}

export function useUpdateStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stageId, input }: { stageId: string; input: UpdateStageInput }) =>
      updateStage(stageId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LEAD_PIPELINES_KEY });
    },
  });
}

export function useDeleteStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (stageId: string) => deleteStage(stageId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LEAD_PIPELINES_KEY });
    },
  });
}

export function useReorderStages() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pipelineId, stageIds }: { pipelineId: string; stageIds: string[] }) =>
      reorderStages(pipelineId, stageIds),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LEAD_PIPELINES_KEY });
    },
  });
}
